import type { MediaAsset } from '@ghostscreen/contracts';
import { formatNumber } from '../../i18n/format';
import { i18n } from '../../i18n/i18n';

export type ContentLibraryFilter = 'all' | 'photos' | 'videos';

export const CONTENT_LIBRARY_FILTERS: readonly ContentLibraryFilter[] = ['all', 'photos', 'videos'];

export const CONTENT_TILE_SIZE_MIN = 110;
export const CONTENT_TILE_SIZE_MAX = 300;
export const CONTENT_TILE_SIZE_DEFAULT = 170;

/** Default dwell when adding an image to a playlist from the Content lightbox. */
export const CONTENT_PLAYLIST_IMAGE_DURATION_SECONDS = 10;

export function filterContentLibrary(
  assets: MediaAsset[],
  filter: ContentLibraryFilter,
): MediaAsset[] {
  if (filter === 'photos') return assets.filter((asset) => asset.kind === 'image');
  if (filter === 'videos') return assets.filter((asset) => asset.kind === 'video');
  return assets;
}

export function formatMediaByteSize(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return i18n.t('content:size.bytes', { value: formatNumber(Math.round(bytes)) });
  const oneDecimal = { minimumFractionDigits: 1, maximumFractionDigits: 1 };
  if (bytes < 1_048_576) {
    return i18n.t('content:size.kilobytes', { value: formatNumber(bytes / 1024, oneDecimal) });
  }
  return i18n.t('content:size.megabytes', { value: formatNumber(bytes / 1_048_576, oneDecimal) });
}

/** `m:ss` duration badge for video tiles — matches design Content grid. */
export function formatMediaDurationBadge(seconds: number | null | undefined): string | null {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return null;
  const total = Math.round(seconds);
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${formatNumber(mins, { useGrouping: false })}:${formatNumber(secs, { minimumIntegerDigits: 2 })}`;
}

export function contentKindLabel(kind: MediaAsset['kind']): string {
  return kind === 'video' ? i18n.t('content:kind.video') : i18n.t('content:kind.image');
}

export function clampContentTileSize(value: number): number {
  if (!Number.isFinite(value)) return CONTENT_TILE_SIZE_DEFAULT;
  return Math.min(
    CONTENT_TILE_SIZE_MAX,
    Math.max(CONTENT_TILE_SIZE_MIN, Math.round(value / 10) * 10),
  );
}

export function defaultPlaylistDurationSeconds(asset: MediaAsset): number {
  if (asset.kind === 'video') {
    const probed = asset.durationSeconds;
    if (probed != null && Number.isFinite(probed) && probed > 0) {
      return Math.max(1, Math.round(probed));
    }
  }
  return CONTENT_PLAYLIST_IMAGE_DURATION_SECONDS;
}

export type ContentLibraryGroupId = 'today' | 'this-week' | 'earlier';

export type ContentLibraryGroup = {
  id: ContentLibraryGroupId;
  label: string;
  countLabel: string;
  items: MediaAsset[];
};

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Whole local calendar days between upload day and "today" (0 = today). */
export function uploadAgeInLocalDays(createdAt: string, now = new Date()): number | null {
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return null;
  const deltaMs = startOfLocalDay(now).getTime() - startOfLocalDay(created).getTime();
  return Math.max(0, Math.round(deltaMs / 86_400_000));
}

function groupIdForAge(days: number | null): ContentLibraryGroupId {
  if (days == null || days >= 7) return 'earlier';
  if (days === 0) return 'today';
  return 'this-week';
}

const GROUP_META = [
  { id: 'today', labelKey: 'content:library.groups.today' },
  { id: 'this-week', labelKey: 'content:library.groups.thisWeek' },
  { id: 'earlier', labelKey: 'content:library.groups.earlier' },
] as const satisfies readonly { id: ContentLibraryGroupId; labelKey: string }[];

/**
 * Photos-style sections from Ghost Screen Content.dc.html:
 * Today (0 days), This week (1–6), Earlier (7+). Newest first within each.
 */
export function groupContentLibraryByUploadDate(
  assets: MediaAsset[],
  now = new Date(),
): ContentLibraryGroup[] {
  const buckets: Record<ContentLibraryGroupId, MediaAsset[]> = {
    today: [],
    'this-week': [],
    earlier: [],
  };

  const sorted = [...assets].sort((a, b) => {
    const aTs = new Date(a.createdAt).getTime();
    const bTs = new Date(b.createdAt).getTime();
    const aSafe = Number.isNaN(aTs) ? 0 : aTs;
    const bSafe = Number.isNaN(bTs) ? 0 : bTs;
    return bSafe - aSafe;
  });

  for (const asset of sorted) {
    buckets[groupIdForAge(uploadAgeInLocalDays(asset.createdAt, now))].push(asset);
  }

  return GROUP_META.filter((meta) => buckets[meta.id].length > 0).map((meta) => {
    const items = buckets[meta.id];
    return {
      id: meta.id,
      label: i18n.t(meta.labelKey),
      countLabel: i18n.t('content:library.itemCount', { count: items.length }),
      items,
    };
  });
}
